const DEBOUNCE_MS = 3000;

const IGNORED_TAGS = new Set(['SCRIPT', 'STYLE', 'LINK', 'HEAD', 'BODY']);
const IGNORED_IDS = new Set(['__nuxt']);
const VUETIFY_PREFIXES = [
  'v-overlay',
  'v-menu__content',
  'v-dialog',
  'v-tooltip',
  'v-snack',
  'v-bottom-sheet',
];

function isVuetifyDetached(el) {
  const id = el.id || '';
  const classes = [...el.classList];
  return VUETIFY_PREFIXES.some(
    (prefix) =>
      id.startsWith(prefix) || classes.some((c) => c.startsWith(prefix))
  );
}

function isExtensionElement(el) {
  if (!(el instanceof HTMLElement)) return false;
  if (IGNORED_TAGS.has(el.tagName)) return false;
  if (IGNORED_IDS.has(el.id)) return false;
  if (isVuetifyDetached(el)) return false;
  return true;
}

function collectElement(el) {
  return {
    id: el.id || null,
    classList: [...el.classList],
    tagName: el.tagName.toLowerCase(),
  };
}

export default ({ store, $axios }) => {
  try {
    const reported = new Set();
    const pending = new Map();
    let debounceTimer = null;

    function addElement(el) {
      if (!isExtensionElement(el)) return;
      const key = `${el.tagName}#${el.id}.${[...el.classList].join('.')}`;
      if (!reported.has(key) && !pending.has(key)) {
        pending.set(key, collectElement(el));
      }
    }

    function report() {
      if (pending.size === 0) return;
      if (!store.state.auth || !store.state.auth.loggedIn) return;
      const elements = [...pending.values()];
      for (const key of pending.keys()) reported.add(key);
      pending.clear();
      $axios.$post('/api/extension-elements.php', { elements }).catch(() => {});
    }

    function scheduleReport() {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(report, DEBOUNCE_MS);
    }

    // Watch for login — flush pending elements when user authenticates
    store.watch(
      (state) => state.auth && state.auth.loggedIn,
      (loggedIn) => {
        if (loggedIn) {
          scheduleReport();
        }
      }
    );

    // Scan existing children
    for (const el of document.documentElement.children) {
      addElement(el);
    }
    for (const el of document.body.children) {
      addElement(el);
    }

    const callback = (mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          addElement(node);
        }
      }
      if (pending.size > 0) {
        scheduleReport();
      }
    };

    const htmlObserver = new MutationObserver(callback);
    const bodyObserver = new MutationObserver(callback);

    htmlObserver.observe(document.documentElement, { childList: true });
    bodyObserver.observe(document.body, { childList: true });

    // If elements were found on init, schedule report
    if (pending.size > 0) {
      scheduleReport();
    }
  } catch (_) {
    // Silent failure — non-critical feature
  }
};
